// Proactively warm the base-map cache for a trail's area so the map still
// renders when a player goes offline in the field.
//
// The map only fetches (and thus lazily CacheFirst-caches, via sw.ts's
// tiles.openfreemap.org route) the tiles the user actually pans/zooms over. A
// player who loads a trail, then walks into a dead zone WITHOUT having panned
// around, gets a blank map. This fills in the tile pyramid + style/sprite/glyphs
// around the trail bounds AT LOAD, while still online.
//
// Design: every fetch here is a plain client-side GET. The existing
// `url.origin === 'https://tiles.openfreemap.org'` CacheFirst route in sw.ts
// intercepts and stores each response in BASE_MAP_CACHE — so this needs NO
// service-worker changes. It's entirely best-effort: any/all failures are
// swallowed, and with no service worker at all it just warms the HTTP cache
// harmlessly. Bounded by MAX_TILES + CONCURRENCY so a large trail can never
// hammer the free OpenFreeMap tile server.

const STYLE_URL = 'https://tiles.openfreemap.org/styles/liberty';
const TILE_ORIGIN = 'https://tiles.openfreemap.org';
const MAX_TILES = 250;
const CONCURRENCY = 6;
// How many zoom levels to prefetch around the trail's natural fit zoom. Lower
// zooms (broader, fewer tiles) are prefetched first so that IF the MAX_TILES cap
// is ever hit, the overview zooms are already covered. In practice, because the
// fit zoom is normalized to the bounds span, a single-source style like
// OpenFreeMap liberty produces a roughly constant ~40 tiles regardless of bounds
// size, so the cap is a safety net for pathological/multi-source cases rather
// than a routinely-hit limit.
const ZOOM_BELOW = 1;
const ZOOM_ABOVE = 2;

export type PrefetchBounds = [[number, number], [number, number]]; // [[minLng,minLat],[maxLng,maxLat]]

// Strict origin check (matches sw.ts's `url.origin === ...` route) — NOT
// startsWith, which would also accept a look-alike suffix origin like
// tiles.openfreemap.org.evil.com. Applied to every URL before fetching.
function isTileOrigin(u: string): boolean {
  try {
    return new URL(u).origin === TILE_ORIGIN;
  } catch {
    return false;
  }
}

export function lngToTileX(lng: number, z: number): number {
  return Math.floor(((lng + 180) / 360) * 2 ** z);
}

export function latToTileY(lat: number, z: number): number {
  const rad = (lat * Math.PI) / 180;
  return Math.floor(
    ((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * 2 ** z
  );
}

function clampTile(v: number, z: number): number {
  const max = 2 ** z - 1;
  return Math.max(0, Math.min(max, v));
}

/** Natural zoom where the bounds' larger span fills roughly one tile. */
export function fitZoom(bounds: PrefetchBounds): number {
  const [[minLng, minLat], [maxLng, maxLat]] = bounds;
  const span = Math.max(Math.abs(maxLng - minLng), Math.abs(maxLat - minLat));
  if (!(span > 0)) return 15; // a single point → a sensible neighbourhood zoom
  // 360° spans the whole world at z0; each zoom halves the degrees per tile.
  const z = Math.log2(360 / span);
  return Math.round(z);
}

/** Fetch `urls` in bounded-concurrency batches, swallowing every error. */
async function fetchAll(urls: string[]): Promise<void> {
  let i = 0;
  const worker = async (): Promise<void> => {
    while (i < urls.length) {
      const url = urls[i++];
      try {
        // no-store on the fetcher would defeat the SW cache; a normal GET lets
        // the CacheFirst route both serve-and-store.
        const res = await fetch(url, { mode: 'cors' });
        // Drain the body so the connection frees and the SW can cache it.
        if (res && res.ok) await res.arrayBuffer();
      } catch {
        // best-effort
      }
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(CONCURRENCY, urls.length) }, worker)
  );
}

interface VectorSource {
  tilesTemplate: string;
  minzoom: number;
  maxzoom: number;
}

/** Resolve the style's vector tile templates + the sprite/glyph URLs. */
async function readStyle(): Promise<{
  sources: VectorSource[];
  extras: string[];
}> {
  const extras: string[] = [];
  const sources: VectorSource[] = [];
  const res = await fetch(STYLE_URL, { mode: 'cors' });
  if (!res.ok) return { sources, extras };
  const style = (await res.json()) as {
    sources?: Record<string, unknown>;
    sprite?: string;
    glyphs?: string;
    layers?: Array<{ layout?: Record<string, unknown> }>;
  };

  // Sprite: a base URL; fetch its json+png at 1x and 2x (cheap, 4 requests).
  if (typeof style.sprite === 'string') {
    for (const suffix of ['.json', '.png', '@2x.json', '@2x.png']) {
      extras.push(`${style.sprite}${suffix}`);
    }
  }

  // Glyphs: prefetch the Latin range (0-255) for each font stack the style
  // references. Other ranges stay lazy (CacheFirst) — missing a range only
  // drops some labels, it never blanks the map.
  if (typeof style.glyphs === 'string') {
    const fontstacks = new Set<string>();
    for (const layer of style.layers ?? []) {
      const font = layer.layout?.['text-font'];
      if (Array.isArray(font) && font.every((f) => typeof f === 'string')) {
        fontstacks.add((font as string[]).join(','));
      }
    }
    for (const stack of fontstacks) {
      extras.push(
        style.glyphs
          .replace('{fontstack}', encodeURIComponent(stack))
          .replace('{range}', '0-255')
      );
    }
  }

  // Vector sources: each carries either an inline `tiles` template or a `url`
  // pointing at a TileJSON we must fetch to get the template + zoom range.
  for (const src of Object.values(style.sources ?? {})) {
    if (!src || typeof src !== 'object') continue;
    const s = src as {
      type?: string;
      tiles?: unknown;
      url?: unknown;
      minzoom?: unknown;
      maxzoom?: unknown;
    };
    if (s.type !== 'vector') continue;
    if (Array.isArray(s.tiles) && typeof s.tiles[0] === 'string') {
      sources.push({
        tilesTemplate: s.tiles[0],
        minzoom: typeof s.minzoom === 'number' ? s.minzoom : 0,
        maxzoom: typeof s.maxzoom === 'number' ? s.maxzoom : 14,
      });
    } else if (typeof s.url === 'string') {
      try {
        const tjRes = await fetch(s.url, { mode: 'cors' });
        if (!tjRes.ok) continue;
        const tj = (await tjRes.json()) as {
          tiles?: unknown;
          minzoom?: unknown;
          maxzoom?: unknown;
        };
        if (Array.isArray(tj.tiles) && typeof tj.tiles[0] === 'string') {
          sources.push({
            tilesTemplate: tj.tiles[0],
            minzoom: typeof tj.minzoom === 'number' ? tj.minzoom : 0,
            maxzoom: typeof tj.maxzoom === 'number' ? tj.maxzoom : 14,
          });
        }
      } catch {
        // best-effort
      }
    }
  }

  return { sources, extras };
}

/**
 * Warm the base-map cache for `bounds`. Safe to call on every trail load: it
 * only fetches https OpenFreeMap URLs, is capped/throttled, and swallows all
 * errors. No-ops off-origin or when bounds are degenerate.
 */
export async function prefetchMapTiles(
  bounds: PrefetchBounds | null | undefined
): Promise<void> {
  if (typeof window === 'undefined' || typeof fetch === 'undefined') return;
  if (!bounds) return;
  const [[minLng, minLat], [maxLng, maxLat]] = bounds;
  if (![minLng, minLat, maxLng, maxLat].every(Number.isFinite)) return;

  let style: { sources: VectorSource[]; extras: string[] };
  try {
    style = await readStyle();
  } catch {
    return;
  }
  if (style.sources.length === 0) {
    // Still warm the style extras (sprite/glyphs) if we got them — origin-
    // filtered, same as the tile path below.
    await fetchAll(style.extras.filter(isTileOrigin));
    return;
  }

  const base = fitZoom(bounds);
  const tileUrls: string[] = [];

  // Prefetch from the lowest (broadest) zoom upward so a trail too big to fully
  // cover still gets complete overview coverage before the cap is reached.
  outer: for (const src of style.sources) {
    // Clamp the natural fit zoom into this source's [minzoom, maxzoom] BEFORE
    // deriving the prefetch window. A small-span trail can have a fitZoom well
    // past the source's maxzoom (e.g. 14 on OpenFreeMap) — without clamping
    // FIRST, `base - ZOOM_BELOW` can still exceed maxzoom, so zLo > zHi and the
    // tile loop below never runs, leaving the offline map blank. MapLibre
    // overzooms the deepest available tiles at closer zooms, so warming the
    // clamped (shallower) zoom range still correctly covers the trail.
    const clampedBase = Math.max(src.minzoom, Math.min(src.maxzoom, base));
    const zLo = Math.max(src.minzoom, clampedBase - ZOOM_BELOW);
    const zHi = Math.min(src.maxzoom, clampedBase + ZOOM_ABOVE);
    for (let z = zLo; z <= zHi; z++) {
      const xLo = clampTile(lngToTileX(minLng, z), z);
      const xHi = clampTile(lngToTileX(maxLng, z), z);
      // Latitude increases → tile Y decreases, so maxLat yields the smaller y.
      const yLo = clampTile(latToTileY(maxLat, z), z);
      const yHi = clampTile(latToTileY(minLat, z), z);
      for (let x = xLo; x <= xHi; x++) {
        for (let y = yLo; y <= yHi; y++) {
          if (tileUrls.length >= MAX_TILES) break outer;
          tileUrls.push(
            src.tilesTemplate
              .replace('{z}', String(z))
              .replace('{x}', String(x))
              .replace('{y}', String(y))
          );
        }
      }
    }
  }

  // Only touch the OpenFreeMap origin (defence in depth) — see isTileOrigin.
  const urls = [...style.extras, ...tileUrls].filter(isTileOrigin);
  await fetchAll(urls);
}
